import { BRAND_OG_IMAGE_URL, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { getHomepageSeoCopy } from "@/lib/brandSeoCopy";
import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { BaseTemplateInternational } from "@/components/templates/BaseTemplateInternational";
import { BioLinkTemplate } from "@/components/templates/BioLinkTemplate";
import { ArgentinaBioTemplate } from "@/components/templates/ArgentinaBioTemplate";
import { MaintenanceTemplate } from "@/components/templates/MaintenanceTemplate";
import BioTemplatePremium from "@/components/templates/BioTemplatePremium";
// BioTemplatePremiumSimple deprecado — home global es BaseTemplate.tsx.
import DualCTAVideoLanding from "@/pages/DualCTAVideoLanding";
import {
  getCurrencyForCountry,
  getPlansForCurrency,
  isPremiumSpecialOfferActive,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  PREMIUM_INVITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { isCloudinaryImage, isSameCloudinaryImage } from "@/lib/cloudinaryUtils";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useSEO } from "@/hooks/useSEO";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { usePremiumInviteFreeTelegramPublicUrl } from "@/hooks/usePremiumInviteFreeTelegramPublicUrl";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  BESTFRIEND_INSTAGRAM_CONFIG,
  getBestFriendInstagramConfigForCountry,
} from "@/config/bestFriendInstagramConfig";
import { getGalleryPhotos } from "@/config/galleryConfig";
import { useEffect, useMemo, useState } from "react";
// Development components
import { ArgentinaProvinceAdmin } from "@/components/dev/ArgentinaProvinceAdmin";

export const Index = () => {
  const templateSelection = useTemplateSelection();
  const { templateName, country, language } = templateSelection;
  const { setTemplateName } = useCurrentTemplate();

  const premiumSimpleHome = templateName === "BioTemplatePremiumSimple";
  const {
    url: premiumSimpleFreeTelegramUrl,
    publicConfig: premiumInvitePublicConfig,
    ready: premiumSimpleConfigReady,
  } = usePremiumInviteFreeTelegramPublicUrl(premiumSimpleHome);

  // PostHog + session replay: expose template for pageview/route_change (all templates)
  useEffect(() => {
    setTemplateName(templateName ?? null);
    return () => setTemplateName(null);
  }, [templateName, setTemplateName]);

  // Site settings from admin (BaseTemplate: header URL, title, description, gallery)
  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  // Portfolio sections from admin - solo para ArgentinaBioTemplate/BioLinkTemplate (BaseTemplate usa solo site settings)
  const {
    allImageUrls: portfolioImageUrls,
    isLoading: portfolioLoading,
    showPortfolioNavLink,
  } = usePortfolioSections();

  // Timeout de seguridad: si después de 8 segundos aún está cargando, forzar render
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (siteSettingsLoading || portfolioLoading) {
        console.warn('[Index] Loading timeout reached, forcing render with defaults');
        setForceRender(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [siteSettingsLoading, portfolioLoading]);

  // SEO home: tono profesional LATAM por defecto (sin Telegram / premium en meta).
  // BaseTemplate puede sobreescribir con site-settings del admin.
  const isBaseTemplate = !["ArgentinaBioTemplate", "BioLinkTemplate", "MaintenanceTemplate", "BioTemplatePremium", "BioTemplatePremiumSimple", "DualCTAVideoLanding"].includes(templateName ?? "");
  const brandSeo = getHomepageSeoCopy("es");
  const seoConfig = {
    title: isBaseTemplate && siteSettings.title?.trim()
      ? siteSettings.title
      : brandSeo.title,
    description: isBaseTemplate && siteSettings.description?.trim()
      ? siteSettings.description
      : brandSeo.description,
    keywords: brandSeo.keywords,
    ogImage: BRAND_OG_IMAGE_URL,
    ogTitle: isBaseTemplate && siteSettings.title?.trim()
      ? siteSettings.title
      : brandSeo.ogTitle,
    ogDescription: isBaseTemplate && siteSettings.description?.trim()
      ? siteSettings.description
      : brandSeo.ogDescription,
    canonical: `${BRAND_SITE_URL}/`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: "website",
    locale: brandSeo.htmlLocale,
    alternateLocale: "en_US",
    // Homepage: index, follow for crawlers (BaseTemplate and other public templates)
    robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
  };

  useSEO(seoConfig);

  // Workaround: VITE_GALLERY_PHOTOS (comma-separated URLs) alimenta la galería si está definido
  const envGalleryPhotoItems = useMemo(
    () =>
      getGalleryPhotos().map((item, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: item.src.trim(),
        title: "",
      })),
    []
  );
  const useEnvGalleryPhotos = envGalleryPhotoItems.length > 0;

  // Gallery: admin es la autoridad (portfolio → site settings); VITE_GALLERY_PHOTOS es solo fallback
  const siteGalleryUrls = (siteSettings.galleryImageUrls ?? [])
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0 && isCloudinaryImage(u))
    .map((u) => u.trim());
  const logoUrl = (siteSettings.logoUrl as string)?.trim() || "";
  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;

  const portfolioUrls = portfolioImageUrls
    .filter((u) => u?.trim() && isCloudinaryImage(u))
    .map((u) => u.trim());
  const portfolioWithoutLogo = logoUrl
    ? portfolioUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : portfolioUrls;

  // Galería para ArgentinaBioTemplate/BioLinkTemplate: admin (portfolio → site settings)
  // es la autoridad; VITE_GALLERY_PHOTOS pasa a ser SOLO fallback si la DB está vacía.
  const galleryMediaItems = !portfolioLoading && portfolioWithoutLogo.length > 0
    ? (portfolioWithoutLogo.length > 1 ? portfolioWithoutLogo.slice(1) : portfolioWithoutLogo).map((src, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: src.trim(),
        title: "",
      }))
    : galleryWithoutLogo.length > 0
      ? (galleryWithoutLogo.length > 1 ? galleryWithoutLogo.slice(1) : galleryWithoutLogo).map((src, i) => ({
          id: i + 1,
          type: "photo" as const,
          src: src.trim(),
          title: "",
        }))
      : !portfolioLoading && useEnvGalleryPhotos
        ? envGalleryPhotoItems
        : [];

  // Política home: BaseTemplate.tsx para todos (site-settings / BaseTemplateAdmin).
  // Sin lock por país. DualCTA solo si el edge/admin inyectó DualCTAVideoLanding.
  const policyTemplateName = useMemo(() => {
    const name = templateName || "BaseTemplate";

    if (
      name === "BaseTemplateInternational" ||
      name === "ArgentinaBioTemplate" ||
      name === "BioLinkTemplate" ||
      name === "MaintenanceTemplate" ||
      name === "BioTemplatePremium"
    ) {
      return name;
    }

    if (name === "DualCTAVideoLanding") {
      return "DualCTAVideoLanding";
    }

    // Cualquier otro nombre (legacy) → BaseTemplate.tsx
    return "BaseTemplate";
  }, [templateName]);

  const renderTemplate = () => {
    if (policyTemplateName === "DualCTAVideoLanding") {
      return <DualCTAVideoLanding />;
    }

    if (policyTemplateName === "ArgentinaBioTemplate") {
      return <ArgentinaBioTemplate mediaItems={galleryMediaItems} />;
    }

    if (policyTemplateName === "BioLinkTemplate") {
      return <BioLinkTemplate mediaItems={galleryMediaItems} />;
    }

    if (policyTemplateName === "MaintenanceTemplate") {
      return <MaintenanceTemplate />;
    }

    // Override por país a BaseTemplateInternational (home editorial intl). Explícito
    // porque para no-AR `useInternationalLayout` puede venir false (showWishlist se
    // suprime en el exterior), y sin esto caería a <BaseTemplate> (español).
    if (policyTemplateName === "BaseTemplateInternational") {
      const baseTemplateLoading = !forceRender && siteSettingsLoading;
      return (
        <BaseTemplateInternational
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
        />
      );
    }

    // Home global: siempre BaseTemplate.tsx (no Black, sin lock de país).
    // No forzar showGallery/showTally: toggles de BaseTemplateAdmin son autoridad.
    if (policyTemplateName === "BaseTemplate") {
      const baseTemplateLoading = !forceRender && siteSettingsLoading;
      return (
        <BaseTemplate
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
        />
      );
    }

    if (policyTemplateName === "BioTemplatePremium") {
      const currency = getCurrencyForCountry(country);
      const { standardPlan, vipPlan } = getPlansForCurrency(currency, language);
      const { successPath, cancelPath } = getPremiumInvitePaths(currency);
      const bestfriendInstagram =
        getBestFriendInstagramConfigForCountry(country) ??
        (isPremiumSpecialOfferActive() ? BESTFRIEND_INSTAGRAM_CONFIG : null);

      return (
        <BioTemplatePremium
          currency={currency}
          country={country}
          language={language}
          standardPlan={standardPlan}
          vipPlan={vipPlan}
        successPath={successPath}
        cancelPath={cancelPath}
        profileName={BRAND_PERSON_NAME}
        flowName={PREMIUM_INVITE_FLOW_NAME}
        bestfriendInstagram={bestfriendInstagram ?? undefined}
        />
      );
    }

    if (policyTemplateName === "BioTemplatePremiumSimple") {
      const baseTemplateLoading = !forceRender && siteSettingsLoading;
      return (
        <BaseTemplate
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
        />
      );
    }

    // Fallback: siempre BaseTemplate.tsx (site-settings).
    const baseTemplateLoading = !forceRender && siteSettingsLoading;
    if (getServerTemplateConfig()?.useInternationalLayout === true) {
      return (
        <BaseTemplateInternational
          mediaItems={[]}
          bannerDeferred={baseTemplateLoading}
          galleryDeferred={baseTemplateLoading}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
        />
      );
    }

    return (
      <BaseTemplate
        mediaItems={[]}
        bannerDeferred={baseTemplateLoading}
        galleryDeferred={baseTemplateLoading}
        showSobreMiOnHome={false}
        showPortfolioNavLink={showPortfolioNavLink}
      />
    );
  };

  // Render template immediately - no loading screen
  return (
    <>
      {renderTemplate()}
      <ArgentinaProvinceAdmin />
    </>
  );
};

export default Index;